/**
 * QUICK BUY DEDUPE — keeps one quick-buy drawer per product, per page.
 *
 * The theme fills a quick-buy drawer with markup fetched from the product page, and that markup carries ids built
 * from the product page's own section: the form `quick-buy-form-<product>-<section>` and
 * `<product-rerender id="quick-buy-modal-content">`. Every drawer for the same product therefore ends up with the
 * same ids, and the theme resolves those ids globally:
 *
 *   * `variant-picker` and `product-rerender` both look their form up with `document.forms[id]`, which returns the
 *     FIRST form in the document — not the one inside the drawer you are looking at;
 *   * `product:rerender` is dispatched once with one `htmlFragment`, and the first handler moves the matching node
 *     out of it, so every later handler finds nothing and silently leaves its drawer stale.
 *
 * The visible popup then keeps its old price, media and add-to-cart while an invisible copy updates instead.
 * `<label for>` inside the duplicated markup points at the first copy's inputs too, so the swatch you click does
 * not even look selected.
 *
 * Duplicates are not a template bug we can fix in Liquid alone: the same product legitimately appears twice on a
 * page (a product that sits in two tabs of one collection section, a mega menu promo rendered into both the
 * desktop menu and the mobile navigation drawer, a Shop the look hotspot for a product that is also in the grid
 * below). Scoping the ids per card — see `card_scope` in `snippets/product-card.liquid` — stops several drawers
 * from opening on a single click, but it cannot make the FETCHED markup unique, because that markup comes from the
 * product page as-is.
 *
 * So instead of letting duplicates coexist, we collapse them: the first drawer of a product survives, the rest are
 * removed, and the buttons that pointed at them are repointed to the survivor. A drawer moves itself into <body>
 * when it opens, so the survivor works from anywhere — including a hidden tab panel or a closed mega menu.
 *
 * Nothing happens on pages where each product is rendered once, which is the overwhelming majority of them.
 */

(() => {
  const DRAWER_TAG = 'quick-buy-drawer';

  /** The product a drawer loads, without the `?variant=` suffix the different call sites disagree on. */
  const productOf = (drawer) => (drawer.getAttribute('handle') || '').split('?')[0];

  /** Ids of drawers this script removed, mapped to the product they belonged to. */
  const collapsed = new Map();

  const collapse = () => {
    const groups = new Map();

    document.querySelectorAll(DRAWER_TAG).forEach((drawer) => {
      const product = productOf(drawer);

      if (!product) {
        return;
      }

      if (!groups.has(product)) {
        groups.set(product, []);
      }

      groups.get(product).push(drawer);
    });

    groups.forEach((drawers, product) => {
      if (drawers.length < 2) {
        return;
      }

      // Never touch a group while one of its drawers is on screen: removing it would yank the popup from under
      // the customer. The pass runs again once it closes.
      if (drawers.some((drawer) => drawer.hasAttribute('open'))) {
        return;
      }

      drawers.slice(1).forEach((duplicate) => {
        if (duplicate.id) {
          collapsed.set(duplicate.id, product);
        }

        duplicate.remove();
      });
    });

    // Point the orphaned buttons at whichever drawer currently serves their product. Done from the ledger rather
    // than inline with the removal, so that a section re-rendered later — the theme editor replacing the header,
    // taking a surviving drawer with it — hands its buttons over to the replacement instead of leaving them
    // aimed at an id that is no longer in the page.
    collapsed.forEach((product, orphanedId) => {
      const survivor = (groups.get(product) || [])[0];

      if (!survivor || !survivor.id || document.getElementById(orphanedId)) {
        return;
      }

      document.querySelectorAll(`[aria-controls="${orphanedId}"]`).forEach((control) => {
        control.setAttribute('aria-controls', survivor.id);
      });
    });
  };

  let scheduled = false;

  const schedule = () => {
    if (scheduled) {
      return;
    }

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      collapse();
    });
  };

  // Sections can arrive after the first paint — the theme editor re-renders them, and the cart drawer pulls its
  // recommendations in over the Section Rendering API — so watch for drawers that show up later. Only additions
  // schedule a pass, and a pass that finds nothing to collapse mutates nothing, so this cannot feed itself.
  const observer = new MutationObserver((mutations) => {
    const addedDrawer = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches(DRAWER_TAG) || node.querySelector(DRAWER_TAG) !== null)
      )
    );

    if (addedDrawer) {
      schedule();
    }
  });

  const start = () => {
    collapse();
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('dialog:after-hide', schedule);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
